<template>
    <div
        ref="container"
        class="feature-discovery-container"
    >
        <transition name="tooltip-fade">
            <div
                v-if="visible"
                class="feature-discovery-tooltip"
                :class="[`arrow-${arrow_position}`]"
                :style="tooltip_styles"
            >
                <div class="tooltip-content">
                    <div class="tooltip-message">
                        <v-icon small :color="text_color" class="mr-2">mdi-information</v-icon>
                        <span>
                            <slot>{{ message }}</slot>
                        </span>
                    </div>
                    <v-btn
                        icon
                        x-small
                        dark
                        class="tooltip-close"
                        @click="onDismiss"
                    >
                        <v-icon small>mdi-close</v-icon>
                    </v-btn>
                </div>
                
                <div class="tooltip-arrow" />
            </div>
        </transition>
        
        <slot name="target" />
    </div>
</template>

<script>
export default {
    name: 'FeatureDiscoveryTooltip',

    props: {
        storage_key: {
            type: String,
            required: true,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        message: {
            type: String,
            default: 'Some options have moved to this improved menu.',
        },
        // % of viewport height to inset from top/bottom before triggering
        // 0 = trigger immediately when visible, 50 = trigger when in middle 50% of viewport
        viewport_trigger_inset_percentage: {
            type: Number,
            default: 0,
        },
        arrow_position: {
            type: String,
            default: 'bottom-right',
            validator: (v) => ['bottom-left', 'bottom-center', 'bottom-right', 'top-left', 'top-center', 'top-right'].includes(v),
        },
        color: {
            type: String,
            default: 'var(--v-secondary-base)',
        },
        text_color: {
            type: String,
            default: 'white',
        },
    },

    data() {
        return {
            visible: false,
            has_been_dismissed: false,
            scroll_triggered: false,
        };
    },

    computed: {
        full_storage_key() {
            return 'rc-feature-discovery-' + this.storage_key;
        },

        tooltip_styles() {
            return {
                '--tooltip-bg': this.color,
                '--tooltip-text': this.text_color,
            };
        },
    },

    watch: {
        disabled: {
            immediate: false,
            handler(new_disabled, old_disabled) {
                if (!new_disabled && old_disabled) {
                    // disabled changed from true to false - set up observer
                    this.has_been_dismissed = window.localStorage.getItem(this.full_storage_key) === 'true';
                    if (!this.has_been_dismissed) {
                        this.$nextTick(() => {
                            this.setupScrollObserver();
                        });
                    }
                } else if (new_disabled && !old_disabled) {
                    // disabled changed from false to true - clean up
                    this.visible = false;
                    this.cleanupScrollObserver();
                }
            },
        },
    },

    mounted() {
        if (this.disabled) return;
        this.has_been_dismissed = window.localStorage.getItem(this.full_storage_key) === 'true';
        if (!this.has_been_dismissed) {
            this.$nextTick(() => {
                this.setupScrollObserver();
            });
        }
    },

    beforeDestroy() {
        this.cleanupScrollObserver();
    },

    methods: {
        setupScrollObserver() {
            if (!this.$refs.container) return;

            const root_margin = this.viewport_trigger_inset_percentage > 0
                ? `-${this.viewport_trigger_inset_percentage}% 0px -${this.viewport_trigger_inset_percentage}% 0px`
                : '0px';

            this.observer = new IntersectionObserver(
                (entries) => {
                    entries.forEach((entry) => {
                        if (entry.isIntersecting) {
                            this.onScrollTriggered();
                        }
                    });
                },
                {
                    threshold: 0,
                    rootMargin: root_margin,
                }
            );

            this.observer.observe(this.$refs.container);
        },

        cleanupScrollObserver() {
            if (this.observer) {
                this.observer.disconnect();
                this.observer = null;
            }
        },

        onScrollTriggered() {
            if (this.scroll_triggered || this.has_been_dismissed) return;
            setTimeout(() => {
                this.scroll_triggered = true;
                this.visible = true;
                this.cleanupScrollObserver();
            }, 500);
        },

        onDismiss() {
            this.visible = false;
            this.has_been_dismissed = true;
            window.localStorage.setItem(this.full_storage_key, 'true');
            this.$emit('dismissed');
        },
    },
};
</script>

<style scoped>
.feature-discovery-container {
    display: inline-block;
    position: relative;
}

.feature-discovery-tooltip {
    position: absolute;
    z-index: 4;
    min-width: 280px;
    max-width: 380px;
}

.tooltip-content {
    background: var(--tooltip-bg);
    color: var(--tooltip-text);
    padding: 12px 16px;
    border-radius: 8px;
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.25);
    display: flex;
    align-items: flex-start;
    gap: 8px;
}

.tooltip-message {
    display: flex;
    align-items: flex-start;
    flex: 1;
    font-size: 14px;
    line-height: 1.4;
}

.tooltip-close {
    flex-shrink: 0;
    margin: -4px -8px -4px 0;
}

.tooltip-arrow {
    position: absolute;
    width: 0;
    height: 0;
    border-style: solid;
}

.arrow-bottom-right {
    bottom: 100%;
    right: 0;
    margin-bottom: 12px;
}

.arrow-bottom-right .tooltip-arrow {
    bottom: -10px;
    right: 20px;
    border-width: 10px 10px 0 10px;
    border-color: var(--tooltip-bg) transparent transparent transparent;
}

.arrow-bottom-center {
    bottom: 100%;
    left: 50%;
    transform: translateX(-50%);
    margin-bottom: 12px;
}

.arrow-bottom-center .tooltip-arrow {
    bottom: -10px;
    left: 50%;
    transform: translateX(-50%);
    border-width: 10px 10px 0 10px;
    border-color: var(--tooltip-bg) transparent transparent transparent;
}

.arrow-bottom-left {
    bottom: 100%;
    left: 0;
    margin-bottom: 12px;
}

.arrow-bottom-left .tooltip-arrow {
    bottom: -10px;
    left: 20px;
    border-width: 10px 10px 0 10px;
    border-color: var(--tooltip-bg) transparent transparent transparent;
}

.arrow-top-right {
    top: 100%;
    right: 0;
    margin-top: 12px;
}

.arrow-top-right .tooltip-arrow {
    top: -10px;
    right: 20px;
    border-width: 0 10px 10px 10px;
    border-color: transparent transparent var(--tooltip-bg) transparent;
}

.arrow-top-center {
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    margin-top: 12px;
}

.arrow-top-center .tooltip-arrow {
    top: -10px;
    left: 50%;
    transform: translateX(-50%);
    border-width: 0 10px 10px 10px;
    border-color: transparent transparent var(--tooltip-bg) transparent;
}

.arrow-top-left {
    top: 100%;
    left: 0;
    margin-top: 12px;
}

.arrow-top-left .tooltip-arrow {
    top: -10px;
    left: 20px;
    border-width: 0 10px 10px 10px;
    border-color: transparent transparent var(--tooltip-bg) transparent;
}

.tooltip-fade-enter-active,
.tooltip-fade-leave-active {
    transition: opacity 0.3s ease, transform 0.3s ease;
}

.tooltip-fade-enter,
.tooltip-fade-leave-to {
    opacity: 0;
    transform: translateY(8px);
}

.arrow-top-right.tooltip-fade-enter,
.arrow-top-center.tooltip-fade-enter,
.arrow-top-left.tooltip-fade-enter,
.arrow-top-right.tooltip-fade-leave-to,
.arrow-top-center.tooltip-fade-leave-to,
.arrow-top-left.tooltip-fade-leave-to {
    transform: translateY(-8px);
}
</style>
